import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'

/**
 * This is where you will add you feature flags to be used within the application.
 * When naming feature flags, ensure the name includes "flag" at the end of the name.
 * i.e: For epic "Neat Feature V1" name the flag -> "neatFeatureV1Flag"
 *
 * Note that when adding a flag, create a corresponding ticket for removing the flag
 * all together.
 */

/** Every feature flag string literal must end with `Flag`. */
type FeatureFlagNameSuffix = `${string}Flag`

export const FEATURE_FLAGS = [
  // SYN-8242 / SYN-8248: when explicitly set, requests pass dailyMemberMetrics
  // true/false so the BE reads from the new tables or the legacy path. Unset
  // omits the param (env default, and rankings stay cached).
  'dailyMemberMetricsFlag',
  // SYN-8122 / SYN-8208: gates the rewritten bulk send award flow until
  // the batch send experience replaces the legacy flow and the flag can be removed.
  'bulkSendAwardV2Flag',
] as const satisfies FeatureFlagNameSuffix[]

export const STORAGE_KEY = 'featureFlag-storage'

export type FeatureFlagName = (typeof FEATURE_FLAGS)[number]

type FeatureFlagStore = {
  flags: Partial<Record<FeatureFlagName, boolean>>
  toggleFlag: (flagName: FeatureFlagName) => void
}

const useFeatureFlagStore = create<FeatureFlagStore>()(
  persist(
    set => ({
      // Unset until the user toggles. Defaulting to `false` would persist an
      // explicit disable and e.g. opt daily member metrics out of BE caching.
      flags: {},
      toggleFlag: (flagName: FeatureFlagName) => {
        set(state => ({
          flags: {
            ...state.flags,
            [flagName]: !state.flags[flagName],
          },
        }))
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      migrate: (persistedState, version) => {
        const state = persistedState as FeatureFlagStore
        if (version === 0) {
          const flags = Object.fromEntries(
            Object.entries(state.flags ?? {}).filter(([, enabled]) => enabled === true),
          )
          return { ...state, flags }
        }
        return state
      },
    },
  ),
)

export const useFeatureFlags = () => {
  const { flags, toggleFlag } = useFeatureFlagStore(
    useShallow(state => ({
      flags: state.flags,
      toggleFlag: state.toggleFlag,
    })),
  )

  return {
    flags,
    isFeatureEnabled: (flagName: FeatureFlagName) => flags[flagName] === true,
    toggleFlag,
  }
}

export default useFeatureFlags
